import React, { useCallback, useEffect, useRef, useState } from "react";

export type PhotoCarouselImage = {
  url: string;
  caption?: string;
};

const AUTO_ADVANCE_MS = 4500;

export default function PhotoCarousel({ images }: { images: PhotoCarouselImage[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback(
    (i: number) => setIndex(((i % images.length) + images.length) % images.length),
    [images.length],
  );

  useEffect(() => {
    if (paused || images.length < 2) return undefined;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused, images.length]);

  if (images.length === 0) return null;

  return (
    <div
      className="relative w-full h-[22rem] sm:h-[28rem] overflow-hidden rounded-2xl bg-secondary-dark"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {images.map((img, i) => (
        <img
          key={img.url}
          src={img.url}
          alt={img.caption || ""}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === index ? 1 : 0 }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

      {images[index]?.caption && (
        <p className="absolute left-6 bottom-6 text-white text-sm sm:text-base font-medium drop-shadow">
          {images[index].caption}
        </p>
      )}

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(index - 1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 hover:bg-white/35 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(index + 1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 hover:bg-white/35 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            ›
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((img, i) => (
              <button
                key={img.url}
                type="button"
                aria-label={`第 ${i + 1} 张`}
                onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === index ? "bg-gold" : "bg-white/50 hover:bg-white/75"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
